"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import SessionBuilder from "./builder";
import { BUILDER_HIGHLIGHTS, SESSION_BUILDER_COPY } from "./data";
import type { ProductVariant } from "./types";

interface Props {
  variant: ProductVariant;
}

export default function ProductSessionBuilderSection({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const copy = SESSION_BUILDER_COPY[variant];

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  return (
    <section
      id="session-builder-section"
      className="full-bleed band-wash py-section border-border overflow-hidden border-b"
      aria-labelledby="session-builder-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
          <motion.div {...fadeUp(0.12)} className="order-2 lg:order-1">
            <SessionBuilder variant={variant} />
          </motion.div>

          <motion.div {...fadeUp(0)} className="order-1 lg:order-2">
            <SectionHeader
              eyebrow={copy.eyebrow}
              id="session-builder-heading"
              heading={copy.heading}
              body={copy.body}
              headingClassName="mb-5"
            />

            <ul className="mt-8 space-y-5">
              {BUILDER_HIGHLIGHTS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.title} className="flex items-start gap-3.5">
                    <span
                      className="border-border bg-foreground/[0.03] flex size-10 shrink-0 items-center justify-center rounded-lg border"
                      aria-hidden="true"
                    >
                      <Icon size={18} className="text-muted-foreground" />
                    </span>
                    <span>
                      <span className="text-foreground block text-sm font-semibold">
                        {item.title}
                      </span>
                      <span className="text-muted-foreground mt-0.5 block text-[13px] leading-relaxed">
                        {item.description}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
